import type { TemplateDraft, TemplateRow } from '../message-templates'

/**
 * The wording of each e-mail the system sends - FRONTEND.md §9.322, BACKEND.md §10A.112.
 *
 * **The owner:** *"Make every email ETC we are using, to be configurable in that nastavenia -> EMAIL,
 * but make it also for all SMS and pushups..."* Push was §10A.83 and every SMS a customer receives
 * already had a template; e-mail had **none**, and the page above this said so in prose - it listed
 * *"Systém posiela e-mail pri: overení registrácie šoféra, registrácii do vernostného programu,
 * potvrdení objednávky…"* and offered a box for two of them.
 *
 * Only the payload reader lives here. Every rule - what counts as an override, what the preview
 * says, which placeholders are real - is `../message-templates`, shared with the push editor,
 * because two copies of one rule is how one of them ends up with a guard the other lacks.
 */

/** The shared row, under this channel's own name - see `../message-templates`. */
export type EmailMessage = TemplateRow

/**
 * `GET /notifications/email-messages`, read by name.
 *
 * `subject` is mapped onto `first` so the shared rules apply unchanged. The screen labels it
 * *Predmet*; the wire keeps the server's own name.
 */
export function emailMessagesOf(payload: unknown): EmailMessage[] {
  if (!Array.isArray(payload)) return []
  return payload
    .map((entry) => {
      const row = (entry ?? {}) as Record<string, unknown>
      return {
        key: typeof row.key === 'string' ? row.key : '',
        label: typeof row.label === 'string' ? row.label : '',
        desc: typeof row.desc === 'string' ? row.desc : '',
        defaultFirst: typeof row.defaultSubject === 'string' ? row.defaultSubject : '',
        defaultBody: typeof row.defaultBody === 'string' ? row.defaultBody : '',
        first: typeof row.subject === 'string' ? row.subject : null,
        body: typeof row.body === 'string' ? row.body : null,
        updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
        placeholders: Array.isArray(row.placeholders)
          ? row.placeholders.filter((name): name is string => typeof name === 'string')
          : [],
      }
    })
    // A row with no key is one no save could address - the endpoint takes the key in its path.
    .filter((row) => row.key !== '')
}

/** Only the two fields the endpoint accepts, so a save cannot carry a label or a default back. */
export function emailPayloadOf(draft: TemplateDraft): Record<string, unknown> {
  return { subject: draft.first.trim(), body: draft.body.trim() }
}

/**
 * Which of these the order confirmation is, because that one's body is two things.
 *
 * §10A.112's rule: **the first line is the heading and the rest is the paragraph**, rendered into an
 * HTML document the franchise does not edit. The screen has to say so where it applies and must not
 * say it where it does not - on the password reset the body is the whole message.
 */
export function bodyIsHeadingAndParagraph(key: string): boolean {
  return key === 'order_confirmation'
}

/** The heading half of a body, as the server splits it. */
export function headingOf(body: string): string {
  const newline = body.indexOf('\n')
  return (newline < 0 ? body : body.slice(0, newline)).trim()
}

/** Everything after the first line. */
export function paragraphOf(body: string): string {
  const newline = body.indexOf('\n')
  return newline < 0 ? '' : body.slice(newline + 1).trim()
}
